import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, UserCheck, Trash2, User, Users, Search } from 'lucide-react';
import { getText, AppLocalEnum } from '@/translate/text';
import { useHasPermission } from '../Utils/helpers.js';
import { useUIStore } from '@/store/uiStore';
import { NotificationService } from '@/services/notificationService';
import { EstimatedRecipientsBar } from '@components/notificationCenter/EstimatedRecipientsBar.jsx';
import { useNavigate } from 'react-router-dom';
import { patientService } from '@/services/patientService';
import { notification_actions } from '@/constants/notificationsActions';

// Components
import ContentStep from './NewNotificationPage/components/ContentStep';
import ReviewStep from './NewNotificationPage/components/ReviewStep';
import UsersList from '@components/notificationCenter/UsersList';

const steps = [
  { id: 'audience', title: getText('audience') || 'Audience' },
  { id: 'content', title: getText('content') || 'Content' },
  { id: 'review', title: getText('review') || 'Review' },
];

const AUDIENCE_OPTIONS = {
  subscription_status: [
    { value: '', label: getText('all') },
    { value: 'subscribe', label: getText('subscribed') },
    { value: 'unsubscribe', label: getText('unsubscribed') },
  ],
  region: [
    { value: '', label: getText('all') },
    { value: 'egypt', label: getText('egypt') },
    { value: 'ksa', label: getText('KSA') },
    { value: 'row', label: getText('rest_of_the_world') },
  ],
  target_condition: [
    { value: '', label: getText('all') },
    { value: 'diabetes', label: getText('diabetes') },
    { value: 'weight_loss', label: getText('weight_loss') },
    { value: 'wellness', label: getText('wellness') },
  ],
  activity_status: [
    { value: '', label: getText('all') },
    { value: 'active_7', label: getText('active_7_days_ago') },
    { value: 'active_14', label: getText('active_14_days_ago') },
    { value: 'inactive_7', label: getText('inactive_7_days_ago') },
    { value: 'inactive_14', label: getText('inactive_14_days_ago') },
    { value: 'inactive_30', label: getText('inactive_30_days_ago') },
  ],
  age_group: [
    { value: '', label: getText('all') },
    { value: '13_24', label: '13-24  years' },
    { value: '25_30', label: '25-30  years' },
    { value: '30_plus', label: '30 +  years' },
  ],
  gender: [
    { value: '', label: getText('all') },
    { value: 'male', label: getText('male') },
    { value: 'female', label: getText('female') },
  ],
};

const NewNotificationPage = () => {
  const { addNotification } = useUIStore();
  const { hasPermission, loading } = useHasPermission('create_notification');
  const language = localStorage.getItem('language') || AppLocalEnum.EN;
  const isRTL = language === AppLocalEnum.AR;

  const [currentStep, setCurrentStep] = useState('audience');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimatedUsers, setEstimatedUsers] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    audience_type: 'single_users',
    subscription_status: '',
    region: '',
    gender: '',
    age_group: '',
    target_condition: '',
    activity_status: '',
    audience_list: [],
    test_audience_list: [],
    // Content fields
    scheduling_type: 'instant',
    language: 'both',
    title_en: '',
    title_ar: '',
    message_en: '',
    message_ar: '',
    image: null,
    cta: notification_actions.HOME_SCREEN.id,
    cta_url: '',
    cta_offer_id: '',
    cta_ad_id: '',
    cta_category_id: '',
    schedule_date: '',
    schedule_time: '',
    notification_id: null,
  });

  const useDebouncedValue = (value, delayMs = 250) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), delayMs);
      return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
  };

  const buildEstimatePayload = (fd) => {
    const payload = {};

    // region: UI egypt/ksa/row -> DTO EG/SA/ROW
    if (fd.region) {
      payload.region =
        fd.region === 'egypt'
          ? 'EG'
          : fd.region === 'ksa'
            ? 'SA'
            : fd.region === 'row'
              ? 'ROW'
              : undefined;
    }

    // ageGroup: UI 13_24/25_30/30_plus -> DTO 13-24/25-30/30+
    if (fd.age_group) {
      payload.age_group =
        fd.age_group === '13_24'
          ? '13-24'
          : fd.age_group === '25_30'
            ? '25-30'
            : fd.age_group === '30_plus'
              ? '30+'
              : undefined;
    }

    // gender: male/female (matches)
    if (fd.gender) payload.gender = fd.gender;

    // activityStatus: active_7 ... (matches your DTO examples)
    if (fd.activity_status) payload.activity_status = fd.activity_status;

    // subscriptionStatus: UI subscribe/unsubscribe/'' -> boolean
    // '' means "all" -> omit field
    if (fd.subscription_status) {
      payload.subscription_status =
        fd.subscription_status === 'subscribe'
          ? true
          : fd.subscription_status === 'unsubscribe'
            ? false
            : undefined;
    }

    if (fd.target_condition) {
      payload.patient_group =
        fd.target_condition === 'diabetes'
          ? 'DM'
          : fd.target_condition === 'weight_loss'
            ? 'LW'
            : fd.target_condition === 'wellness'
              ? 'FB'
              : undefined;
    }

    // remove undefined keys
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    return payload;
  };
  const estimatePayload = useMemo(() => buildEstimatePayload(formData), [formData]);
  const debouncedEstimatePayload = useDebouncedValue(estimatePayload, 250);

  const fetchEstimate = async (bodyObj) => {
    setEstimateLoading(true);
    setEstimateError(null);

    try {
      const data = await NotificationService.estimateUsersNumber(bodyObj);
      setEstimatedUsers(data ?? 0);
    } catch (err) {
      setEstimateError(err?.message || 'Failed to estimate users');
      setEstimatedUsers(null);
    } finally {
      setEstimateLoading(false);
    }
  };
  useEffect(() => {
    if (formData.audience_type !== 'user_group') {
      setEstimatedUsers(null);
      setEstimateError(null);
      setEstimateLoading(false);
      return;
    }
    if (currentStep !== 'audience') return;

    fetchEstimate({});
  }, [formData.audience_type, currentStep]);

  useEffect(() => {
    if (formData.audience_type !== 'user_group' || currentStep !== 'audience') return;
    fetchEstimate(debouncedEstimatePayload);
  }, [debouncedEstimatePayload, formData.audience_type, currentStep]);

  const onChange = (eOrName, maybeValue) => {
    const name = typeof eOrName === 'string' ? eOrName : eOrName?.target?.name;
    const value = typeof eOrName === 'string' ? maybeValue : eOrName?.target?.value;

    if (!name) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setAudienceType = (type) => {
    setFormData((prev) => ({
      ...prev,
      audience_type: type,
      ...(type === 'single_users'
        ? {
            subscription_status: '',
            region: '',
            gender: '',
            age_group: '',
            target_condition: '',
            activity_status: '',
          }
        : { audience_list: [] }),
    }));
  };

  const StepIndicator = () => (
    <div className="mb-8">
      <div
        className="flex justify-between items-center"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        {steps.map((step, index) => {
          const currentIndex = steps.findIndex((s) => s.id === currentStep);
          const isActive = currentStep === step.id;
          const isDone = index < currentIndex;

          return (
            <React.Fragment key={step.id}>
              <div className="flex space-x-4">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center
                    ${isActive ? 'bg-primary-600 text-white' : ''}
                    ${isDone ? 'bg-primary-600 text-white' : ''}
                    ${!isActive && !isDone ? 'bg-gray-200 text-gray-400' : ''}
                  `}
                >
                  {index + 1}
                </div>
                <span className="text-sm font-medium mt-2 items-center">{step.title}</span>
              </div>

              {index < steps.length - 1 && (
                <div
                  className="flex-1 h-1 mx-4 my-auto"
                  style={{
                    background: isDone
                      ? '#22c55e'
                      : isActive
                        ? 'linear-gradient(to right, #22c55e 50%, #e5e7eb 50%)'
                        : '#e5e7eb',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const isNextDisabled = () => {
    if (currentStep === 'audience') {
      if (formData.audience_type === 'single_users') {
        return formData.audience_list.length === 0;
      } else if (formData.audience_type === 'user_group') {
        return false;
      }
    }

    if (currentStep === 'content') {
      const isEnSet =
        (formData.language === 'en' || formData.language === 'both') &&
        (!formData.title_en || !formData.message_en);
      const isArSet =
        (formData.language === 'ar' || formData.language === 'both') &&
        (!formData.title_ar || !formData.message_ar);

      const isScheduleMissing =
        formData.scheduling_type === 'scheduled' &&
        (!formData.schedule_date || !formData.schedule_time);

      return isEnSet || isArSet || isScheduleMissing || !!formData.imageError;
    }

    return false;
  };

  const handleNext = () => {
    if (isNextDisabled()) return;
    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    if (currentIndex < steps.length - 1) setCurrentStep(steps[currentIndex + 1].id);
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    if (currentIndex > 0) setCurrentStep(steps[currentIndex - 1].id);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      console.log('Create notification payload:', formData);

      addNotification({
        type: 'success',
        message:
          getText('notification_created_successfully') || 'Notification created successfully',
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message:
          error?.message ||
          getText('couldn_t_create_notification') ||
          "Couldn't create notification",
      });
      console.error("Couldn't create notification:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const primaryButtonText = () => {
    if (currentStep === 'audience') return getText('continue_to_content') || 'Continue to Content';
    if (currentStep === 'content') return getText('continue_to_review') || 'Continue to Review';
    return;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500" />
          <p className="mt-4 text-gray-700 dark:text-gray-300 text-lg">
            {getText('loading') || 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 text-center">
          <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400">
            {getText('access_denied') || 'Access Denied'}
          </h2>
          <p className="mt-2 text-gray-700 dark:text-gray-300">
            {getText('you_do_not_have_permission_to_access_this_page') ||
              'You do not have permission to access this page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-start items-center">
        <div className="text-xl font-bold px-8">{getText('notification_center')}</div>
      </div>

      <div className="px-8 pt-2 pb-8">
        {/* Breadcrumbs */}

        <nav className="flex items-center space-x-2 text-sm font-medium mb-1">
          <button
            onClick={() => navigate('/notification-center')}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            {getText('notification_center') || 'Notification Center'}
          </button>

          <span className="text-gray-400">»</span>
          <span className="text-gray-600">
            {getText('create_notification') || 'Create Notification'}
          </span>
        </nav>
        {currentStep !== 'audience' && (
          <button
            type="button"
            className="cursor-pointer bg-primary-600 py-2 px-3 rounded-xl text-white flex items-center gap-1 mt-3"
            onClick={handleBack}
          >
            <ChevronLeft color="white" />
            {'Back'}
          </button>
        )}
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-10">
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl px-8 pt-6">
          <StepIndicator />
        </div>

        <div className="my-6 pb-6 bg-white border rounded-2xl shadow-sm">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* 1. Step Rendering */}
              {currentStep === 'audience' && (
                <AudienceStep
                  formData={formData}
                  onChange={onChange}
                  setAudienceType={setAudienceType}
                  isRTL={isRTL}
                  estimateLoading={estimateLoading}
                  estimateError={estimateError}
                  estimatedUsers={estimatedUsers}
                  setFormData={setFormData}
                />
              )}

              {currentStep === 'content' && (
                <ContentStep
                  formData={formData}
                  onChange={onChange}
                  setFormData={setFormData}
                  AUDIENCE_OPTIONS={AUDIENCE_OPTIONS}
                  estimatedUsers={estimatedUsers}
                  estimateLoading={estimateLoading}
                  estimateError={estimateError}
                />
              )}

              {currentStep === 'review' && (
                <ReviewStep
                  formData={formData}
                  setFormData={setFormData}
                  AUDIENCE_OPTIONS={AUDIENCE_OPTIONS}
                  estimatedUsers={estimatedUsers}
                  estimateLoading={estimateLoading}
                  estimateError={estimateError}
                />
              )}

              {/* 2. Step Navigation Button - Hidden if on Review Step */}
              {currentStep !== 'review' && (
                <div
                  className="m-8 flex justify-end mt-8"
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                >
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isSubmitting || isNextDisabled()}
                    className={`
                    px-12 py-2 rounded-xl font-bold transition-all
                    bg-primary-600 hover:bg-primary-700 text-white
                    disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center justify-center gap-2 w-full
                `}
                  >
                    {primaryButtonText()}
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ReviewStep renders its action buttons here via portal */}
        <div id="review-step-actions" />
      </div>
    </div>
  );
};

const AudienceStep = ({
  formData,
  onChange,
  setAudienceType,
  isRTL,
  estimateLoading,
  estimateError,
  estimatedUsers,
  setFormData,
}) => {
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(null);
  const [searchResults, setSearchResults] = React.useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const recipientsNumber =
    formData.audience_type === 'single_users'
      ? formData.audience_list?.length || 0
      : estimateLoading
        ? null
        : (estimatedUsers ?? null);
  const onRemoveFilter = (field) => {
    onChange(field, '');
  };

  const isAdded = (id) => {
    if (!id) return false;
    return formData.audience_list?.some((u) => String(u?._id) === String(id)) ?? false;
  };

  const searchPatients = async (term, targetPage = 1, isNewSearch = true) => {
    setLoadingSearch(true);
    try {
      const response = await patientService.getPatients(targetPage, limit, term, {});
      const newDocs = response.data || [];

      setSearchResults((prev) => (isNewSearch ? newDocs : [...prev, ...newDocs]));
      setTotalPages(response.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error searching patients:', error);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    const isBottom = scrollHeight - scrollTop <= clientHeight + 10;

    if (isBottom && !loadingSearch && (totalPages === null || page < totalPages)) {
      const nextPage = page + 1;
      setPage(nextPage);
      searchPatients(searchTerm, nextPage, false); // false = append mode
    }
  };

  // const searchPatients = async (term) => {
  //   setLoadingSearch(true);
  //   try {
  //     // Use the 'term' passed to the function, and use state variables for page/limit
  //     let response = await patientService.getPatients(page, limit, term, {}, true);

  //     // Safety check: if the term in state changed while this was fetching, ignore this result
  //     setSearchResults(response.data || []);
  //   } catch (error) {
  //     console.error('Error searching patients:', error);
  //   } finally {
  //     setLoadingSearch(false);
  //   }
  // };

  const toggleUserInList = (user) => {
    const targetId = typeof user === 'string' ? user : user._id;

    setFormData((prev) => {
      const isAlreadySelected = prev.audience_list.some((u) => u._id === targetId);

      const newList = isAlreadySelected
        ? prev.audience_list.filter((u) => u._id !== targetId) // 2. Fix variable typo
        : [...prev.audience_list, typeof user === 'string' ? { _id: user } : user]; // 3. Add

      return {
        ...prev,
        audience_list: newList,
      };
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim()) {
        setPage(1);
        searchPatients(searchTerm, 1, true);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const emptyState = (title, description) => {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-center">
          <UserCheck className="w-10 h-10 text-gray-300" />
          <p className="text-md text-gray-400">{title || 'No users exists'}</p>
          <p className="text-sm text-gray-400">{description}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white space-y-6 my-6">
      {/* Section Card */}
      <div className="p-6">
        <h3 className="text-base font-semibold text-gray-900">
          {getText('select_audience') || 'Select Audience'}
        </h3>

        {/* Audience Type Buttons */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Button 1: Single Users */}
          <button
            type="button"
            onClick={() => setAudienceType('single_users')}
            className={`
            flex flex-col items-center justify-center gap-2
            border-2 rounded-xl p-5 text-sm font-medium transition-all duration-200
            ${
              formData.audience_type === 'single_users'
                ? 'bg-primary-50 border-primary-600 text-primary-700 dark:bg-primary-900/30 dark:border-primary-400 dark:text-primary-300'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
            }
            `}
          >
            <User className="w-5 h-5" />
            <div className="text-sm font-semibold">{getText('single_users') || 'Single Users'}</div>
            <div className="text-xs text-gray-500">
              {getText('search_users_hint') || 'Serach by name, phine, id'}
            </div>
          </button>

          {/* Button 2: User Group */}
          <button
            type="button"
            onClick={() => setAudienceType('user_group')}
            className={`
            flex flex-col items-center justify-center gap-2
            border-2 rounded-xl p-5 text-sm font-medium transition-all duration-200
            ${
              formData.audience_type === 'user_group'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }
            `}
          >
            <Users className="w-5 h-5" />
            <div className="text-sm font-semibold">{getText('user_group') || 'User Group'}</div>
            <div className="text-xs text-gray-500">
              {getText('filter_by_criteria') || 'Filter by criteria'}
            </div>
          </button>
        </div>

        {/* Content */}
        {formData.audience_type === 'single_users' ? (
          <div className=" mt-6 border border-gray-200 rounded-xl">
            {/* 1. Search Input Field */}
            <div className="relative">
              <input
                type="text"
                className="form-input py-3 px-8 w-full border  border-1 focus:ring-0"
                placeholder={getText('search_by_name_or_email')}
                value={searchTerm}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  // Timeout allows the "Add" click to register before the list swaps
                  setTimeout(() => setIsFocused(false), 200);
                }}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>

            {/* 2. Dynamic List Area */}
            <div
              onScroll={handleScroll}
              className="overflow-y-auto space-y-2 pr-1 max-h-[600px]  border-t border-gray-100"
            >
              {isFocused && searchTerm.length > 0 ? (
                /* State A: Active Searching */
                loadingSearch ? (
                  <p className="text-sm text-gray-500 flex items-center gap-2 p-2">
                    <span className="animate-spin">🌀</span>{' '}
                    {getText('searching') || 'Searching...'}
                  </p>
                ) : searchResults.length === 0 ? (
                  emptyState(getText('no_users_exists'), '')
                ) : (
                  <UsersList
                    users={searchResults}
                    onAdd={(user) => toggleUserInList(user)}
                    isAdded={(id) => isAdded(id)}
                    hasBorder={false}
                    fromUserSelection={true}
                  />
                )
              ) : /* State B: Selected Users (Show if list is not empty) */
              formData.audience_list.length > 0 ? (
                <div className="space-y-3">
                  {formData.audience_list.map((user) => (
                    <SelectedUserRow
                      key={user._id || user.id}
                      user={user}
                      isRTL={isRTL}
                      onRemove={(user) => toggleUserInList(user)}
                    />
                  ))}
                </div>
              ) : (
                /* State C: Totally Empty */
                <div className="h-0" />
              )}
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <CustomDropdown
                label={getText('subscription_status') || 'Subscription status'}
                name="subscription_status"
                value={formData.subscription_status}
                options={AUDIENCE_OPTIONS.subscription_status}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />

              <CustomDropdown
                label={getText('target_condition') || 'Target Conditions'}
                name="target_condition"
                value={formData.target_condition}
                options={AUDIENCE_OPTIONS.target_condition}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />

              <CustomDropdown
                label={getText('region') || 'Country'}
                name="region"
                value={formData.region}
                options={AUDIENCE_OPTIONS.region}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />

              <CustomDropdown
                label={getText('age_group') || 'Age Group'}
                name="age_group"
                value={formData.age_group}
                options={AUDIENCE_OPTIONS.age_group}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />

              <CustomDropdown
                label={getText('gender') || 'Gender'}
                name="gender"
                value={formData.gender}
                options={AUDIENCE_OPTIONS.gender}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />

              <CustomDropdown
                label={getText('user_activity') || 'Users Activity'}
                name="activity_status"
                value={formData.activity_status}
                options={AUDIENCE_OPTIONS.activity_status}
                onChange={onChange}
                isRTL={isRTL}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />
            </div>
          </div>
        )}

        {/* Estimated Recipients Panel (matches screenshot) */}
        <div className="mt-6">
          <EstimatedRecipientsBar
            formData={formData}
            audienceOptions={AUDIENCE_OPTIONS}
            estimatedUsers={estimatedUsers}
            estimateLoading={estimateLoading}
            estimateError={estimateError}
            onRemoveFilter={onRemoveFilter}
          />
        </div>
      </div>
    </div>
  );
};

const CustomDropdown = ({
  label,
  name,
  value,
  options,
  onChange,
  isRTL,
  openDropdown,
  setOpenDropdown,
}) => {
  const isOpen = openDropdown === name;
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-2">{label}</label>

      <button
        type="button"
        onClick={() => setOpenDropdown(isOpen ? null : name)}
        className={`
          w-full bg-white rounded-lg px-4 py-2
          shadow-sm border border-transparent
          focus:outline-none focus:ring-2 focus:ring-primary-500
          ${isRTL ? 'text-right' : 'text-left'}
        `}
      >
        <span className="text-gray-700">{selected?.label}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-xl overflow-hidden"
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <ul className="max-h-64 overflow-auto">
              {options.map((opt) => {
                const selectedNow = opt.value === value || (value === '' && opt.value === '');

                return (
                  <li key={opt.value ?? opt.label}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(name, opt.value);
                        setOpenDropdown(null);
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedNow) e.currentTarget.style.backgroundColor = '#ecfdf5'; // emerald-50
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedNow) e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                      className={`
                            w-full px-4 py-3 text-sm
                            ${isRTL ? 'text-right' : 'text-left'}
                            ${selectedNow ? 'bg-emerald-100 text-gray-800' : 'text-gray-700'}
                            transition-colors
                        `}
                      style={{ backgroundColor: selectedNow ? '#d1fae5' : '#ffffff' }} // emerald-100 / white
                    >
                      {opt.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SelectedUserRow = ({ user, onRemove, isRTL }) => {
  console.log('user from selected ', user);
  return (
    <div className="flex items-center justify-between p-3 m-3 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition-shadow">
      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* Avatar with Initials */}
        <div className="w-10 h-10 rounded-full bg-[#4CAF50] flex items-center justify-center text-white font-bold text-sm">
          {user.first_name?.[0] + user.last_name?.[0]}
        </div>

        {/* User Details */}
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <p className="text-sm font-bold text-gray-900">
            {user.first_name + ' ' + user.last_name}
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">{user.mobile_number || user.identifier}</span>
            <span className="text-gray-300">•</span>
            {user.is_subscribed && (
              <span className="text-[#4CAF50] font-medium text-[14px]">
                {getText('Subscribed') || 'Subscribed'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* ID Badge */}
        <div className="hidden sm:block px-2 py-1 bg-emerald-50 text-[#4CAF50] text-[12px] font-bold rounded-md border border-emerald-100">
          ID : {user.external_user_id}
        </div>

        {/* Trash Action */}
        <button
          type="button"
          onClick={() => onRemove(user)}
          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default NewNotificationPage;
